// The File Browser's API surface for one workspace (personal or a company),
// with fallbacks for a backend that predates the dashboard endpoints: if
// /v1/vfs/files is missing, listings come from /v1/files/recent and
// /v1/vfs/folders/{id} and are filtered/sorted here.
import {
  acceptVfsFolderSuggestion,
  createVfsFolder,
  deleteVfsFile,
  deleteVfsFolder,
  dismissVfsFolderSuggestion,
  downloadSavedFile,
  fetchFilingPreferences,
  fetchRecentFiles,
  fetchSavedFileBlob,
  fetchVfsFileMetadata,
  fetchVfsFileVersions,
  fetchVfsFiles,
  fetchVfsFolderContents,
  fetchVfsStats,
  fetchVfsTree,
  mergeVfsFolder,
  moveVfsFile,
  moveVfsFolder,
  renameVfsFile,
  renameVfsFolder,
  requestVfsFolderSuggestions,
  saveBlobAs,
  saveFilingPreferences,
  setCompanyAutoSort,
  setLibraryAutoSort,
  shareFileToCompany,
  shareFolderToCompany,
} from '../atlas/atlasApi';
import {
  PAGE_SIZE,
  filterLegacyRows,
  isMissingEndpoint,
  nextLibrarySeq,
  normalizeFileRow,
  sortRows,
} from './fileLibrary';

const LEGACY_RECENT_LIMIT = 50;

export function createLibraryClient({ dataUserId, ownerUserId, workspaceContext, companyId, legacy }) {
  const ctx = { dataUserId: dataUserId || undefined, ownerUserId: dataUserId ? undefined : (ownerUserId || undefined), workspaceContext };
  // Shared across clients of one modal session so a missing endpoint is only probed once.
  const flags = legacy || {};
  let suggestInFlight = null;

  async function legacyList({ q, sourceGroup, kind, folderId, unfiled, sort, offset = 0, limit = PAGE_SIZE }) {
    if (folderId) {
      const data = await fetchVfsFolderContents({ folderId, limit, offset, ...ctx });
      const rows = (data?.files || []).map(normalizeFileRow).filter(Boolean);
      const filtered = sortRows(filterLegacyRows(rows, { q, sourceGroup, kind }), sort);
      return { files: filtered, total: null, hasMore: rows.length >= limit, consumed: rows.length, legacy: true };
    }
    if (offset > 0) return { files: [], total: null, hasMore: false, legacy: true };
    const data = await fetchRecentFiles({ type: 'all', limit: LEGACY_RECENT_LIMIT, ...ctx });
    const rows = (data?.files || []).map(normalizeFileRow).filter(Boolean);
    const filtered = sortRows(filterLegacyRows(rows, { q, sourceGroup, kind, unfiled }), sort);
    return { files: filtered, total: filtered.length, hasMore: false, legacy: true };
  }

  async function listFilesRaw(params = {}) {
    const { offset = 0, limit = PAGE_SIZE, signal } = params;
    if (!flags.noFilesEndpoint) {
      try {
        const data = await fetchVfsFiles({ ...params, offset, limit, signal, ...ctx });
        const files = (data?.files || []).map(normalizeFileRow).filter(Boolean);
        const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : null;
        const hasMore = total != null ? offset + files.length < total : files.length >= limit;
        return { files, total, hasMore, legacy: false };
      } catch (err) {
        if (!isMissingEndpoint(err)) throw err;
        flags.noFilesEndpoint = true;
      }
    }
    return legacyList(params);
  }

  return {
    workspaceContext,
    companyId: companyId || null,

    // Rows carry _fetchSeq (when they were requested) so local patches made
    // later still show, and patches made earlier give way to this data.
    async listFiles(params = {}) {
      const fetchSeq = nextLibrarySeq();
      const result = await listFilesRaw(params);
      result.files = result.files.map((row) => ({ ...row, _fetchSeq: fetchSeq }));
      return { ...result, fetchSeq };
    },

    async stats() {
      if (flags.noStatsEndpoint) return null;
      try {
        return await fetchVfsStats(ctx);
      } catch (err) {
        if (isMissingEndpoint(err)) { flags.noStatsEndpoint = true; return null; }
        throw err;
      }
    },

    tree: () => fetchVfsTree(ctx),

    metadata: (fileId, signal) => fetchVfsFileMetadata({ fileId, signal, ...ctx }),

    async versions(fileId, signal) {
      if (flags.noVersionsEndpoint) return null;
      try {
        return await fetchVfsFileVersions({ fileId, signal, ...ctx });
      } catch (err) {
        if (isMissingEndpoint(err)) { flags.noVersionsEndpoint = true; return null; }
        throw err;
      }
    },

    renameFile: (fileId, filename) => renameVfsFile({ fileId, filename, ...ctx }),
    moveFile: (fileId, folderId) => moveVfsFile({ fileId, folderId: folderId || null, ...ctx }),
    deleteFile: (fileId) => deleteVfsFile({ fileId, ...ctx }),

    // Folder suggestions for unfiled files (FileRow.suggestion). Each request
    // is a model call: leaving Unfiled and coming back while the background
    // request is still running shares it instead of asking again.
    suggestFolders({ fileIds, force } = {}) {
      if (fileIds?.length || force) return requestVfsFolderSuggestions({ fileIds, force, ...ctx });
      if (!suggestInFlight) {
        suggestInFlight = Promise.resolve()
          .then(() => requestVfsFolderSuggestions({ ...ctx }))
          .finally(() => { suggestInFlight = null; });
      }
      return suggestInFlight;
    },
    acceptSuggestion: (fileId) => acceptVfsFolderSuggestion({ fileId, ...ctx }),

    // Auto-sort switch for this library. A backend without the generic route
    // (404) still has the company one.
    setAutoSort: (autoSort) => setLibraryAutoSort({ autoSort, ...ctx }).catch((err) => {
      if (err?.status === 404 && companyId && workspaceContext?.isCompanyScope) {
        return setCompanyAutoSort({ companyId, autoSort });
      }
      throw err;
    }),
    dismissSuggestion: (fileId) => dismissVfsFolderSuggestion({ fileId, ...ctx }),

    // Free-text filing rules for this library (404 on an older backend).
    getFilingPreferences: () => fetchFilingPreferences(ctx),
    saveFilingPreferences: (text) => saveFilingPreferences({ preferences: text, ...ctx }),

    createFolder: (name, parentFolderId) => createVfsFolder({ name, parentFolderId, ...ctx }),
    renameFolder: (folderId, name) => renameVfsFolder({ folderId, name, ...ctx }),
    moveFolder: (folderId, parentFolderId) => moveVfsFolder({ folderId, parentFolderId, ...ctx }),
    mergeFolder: (folderId, targetFolderId) => mergeVfsFolder({ folderId, targetFolderId, ...ctx }),
    deleteFolder: (folderId) => deleteVfsFolder({ folderId, ...ctx }),

    shareFileToCompany: (fileId) => shareFileToCompany({ fileId, companyId, dataUserId: ctx.dataUserId }),
    shareFolderToCompany: (folderId) => shareFolderToCompany({ folderId, companyId, dataUserId: ctx.dataUserId }),

    loadFile: ({ file, variant, signal }) => fetchSavedFileBlob({ fileId: file.file_id, variant, signal, ...ctx }),
    download(file, blob) {
      if (blob) {
        saveBlobAs(blob, file.filename);
        return Promise.resolve();
      }
      return downloadSavedFile({ fileId: file.file_id, filename: file.filename, ...ctx });
    },
  };
}
